import React, { useState, useEffect } from 'react';
import { getOptionsExpirationDates } from '../../services/api';

/**
 * ExpirationDateSelect - Dynamic dropdown for selecting options expiration dates
 *
 * Fetches available expiration dates for a given ticker from Polygon.io API
 * and displays them in a user-friendly dropdown format.
 *
 * Props:
 *   - ticker: Stock ticker symbol (e.g., 'AAPL')
 *   - value: Currently selected expiration date (YYYY-MM-DD format)
 *   - onChange: Callback function when selection changes
 *   - disabled: Whether the dropdown should be disabled
 */
function ExpirationDateSelect({ ticker, value, onChange, disabled }) {
  const [expirationDates, setExpirationDates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch expiration dates whenever ticker changes
  useEffect(() => {
    // Clear dates if no ticker
    if (!ticker || ticker.trim() === '') {
      setExpirationDates([]);
      setError(null);
      return;
    }

    const fetchExpirationDates = async () => {
      setLoading(true);
      setError(null);

      try {
        console.log(`📅 Fetching expiration dates for ${ticker}...`);
        const data = await getOptionsExpirationDates(ticker);

        if (data.expiration_dates && data.expiration_dates.length > 0) {
          setExpirationDates(data.expiration_dates);
          console.log(`✅ Found ${data.expiration_dates.length} expiration dates for ${ticker}`);
        } else {
          setExpirationDates([]);
          setError(`No options contracts found for ${ticker}`);
          console.warn(`⚠️ No expiration dates found for ${ticker}`);
        }
      } catch (err) {
        console.error('❌ Error fetching expiration dates:', err);
        setError(`Failed to fetch expiration dates for ${ticker}`);
        setExpirationDates([]);
      } finally {
        setLoading(false);
      }
    };

    fetchExpirationDates();
  }, [ticker]);

  /**
   * Format date for display in dropdown
   * Converts "2025-01-17" to "Fri, Jan 17, 2025"
   */
  const formatDateForDisplay = (dateString) => {
    if (!dateString) return '';

    try {
      const date = new Date(dateString + 'T00:00:00');  // Add time to avoid timezone issues
      return date.toLocaleDateString('en-US', {
        weekday: 'short',
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch (err) {
      console.error('Error formatting date:', err);
      return dateString;  // Fallback to original string
    }
  };

  return (
    <div>
      <select
        className="input"
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled || loading || !ticker || expirationDates.length === 0}
      >
        {/* Default placeholder option */}
        <option value="">
          {loading
            ? 'Loading dates...'
            : !ticker
            ? 'Select a ticker first'
            : expirationDates.length === 0
            ? 'No expirations available'
            : 'Select expiration date'}
        </option>

        {/* Render expiration dates */}
        {expirationDates.map((date) => (
          <option key={date} value={date}>
            {formatDateForDisplay(date)} ({date})
          </option>
        ))}
      </select>

      {/* Error message display */}
      {error && !loading && (
        <small className="text-xs text-red-400 mt-1 block">{error}</small>
      )}

      {/* Loading indicator */}
      {loading && (
        <small className="text-xs text-gray-400 mt-1 block">
          Fetching available expiration dates...
        </small>
      )}

      {/* Helper text when dates are loaded */}
      {!loading && !error && expirationDates.length > 0 && (
        <small className="text-xs text-gray-400 mt-1 block">
          {expirationDates.length} expiration{expirationDates.length !== 1 ? 's' : ''} available
        </small>
      )}
    </div>
  );
}

export default ExpirationDateSelect;
